/* eslint-disable react/no-unescaped-entities */
/**
 * Follower — admin tool for growing @centralrentals on Facebook,
 * Instagram, and YouTube. Companion to ViralPost.
 *
 * Tabs:
 *   Listener   — recent comments/mentions across all 3 (needs tokens)
 *   Analytics  — follower count + engagement (needs tokens)
 *   Finder     — daily list of accounts to follow + engage with
 *   YT Magnet  — YouTube title/desc/tags/thumbnail generator
 *   Connect    — paste FB Page Token + YouTube API key
 */
import { useEffect, useMemo, useState } from "react";
import {
  Sparkles, Loader2, Lock, Copy, RefreshCw, Shuffle,
  Inbox, BarChart3, Compass, Youtube, Settings, ExternalLink, Image as ImageIcon,
  Facebook, Instagram, CheckCircle2, AlertCircle, Linkedin, Ghost, Target, ListChecks,
} from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const TOKEN_KEY = "cr_admin_token";

function cn(...c) { return c.filter(Boolean).join(" "); }

async function api(path, { token, method = "GET", body } = {}) {
  const r = await fetch(`${API}/api/follower${path}`, {
    method,
    headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "X-Admin-Token": token, ...(body ? { "Content-Type": "application/json" } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (r.status === 401) throw new Error("unauthorized");
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d.detail || `${method} ${path} failed (${r.status})`);
  }
  return r.json();
}

// ---------------------------------------------------------------------------
// Lock screen
// ---------------------------------------------------------------------------
function LockScreen({ onUnlock }) {
  const [v, setV] = useState("");
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-5">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center"><Lock className="w-5 h-5 text-emerald-700"/></div>
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Follower</h1>
            <p className="text-sm text-slate-500">Internal team only</p>
          </div>
        </div>
        <div className="mt-6 space-y-3">
          <label className="text-sm font-semibold text-slate-700">Admin token</label>
          <input type="password" value={v} onChange={(e) => setV(e.target.value)} data-testid="follower-token-input" placeholder="crc-cfg-…" className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm font-mono outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"/>
          <button data-testid="follower-unlock-btn" onClick={() => v.trim() && onUnlock(v.trim())} className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-2.5 rounded-lg text-sm">Unlock</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FINDER tab
// ---------------------------------------------------------------------------
function FinderTab({ token }) {
  const [platform, setPlatform] = useState("youtube");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reseeding, setReseeding] = useState(false);
  const [error, setError] = useState("");

  async function load(p = platform) {
    setLoading(true); setError("");
    try {
      const d = await api(`/finder/today?platform=${p}`, { token });
      setItems(d.items || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(platform); }, [platform]); // eslint-disable-line

  async function reseed() {
    if (!window.confirm("Re-generate the entire account pool for this platform?")) return;
    setReseeding(true); setError("");
    try {
      await api(`/finder/reseed?platform=${platform}`, { token, method: "POST" });
      await load(platform);
    } catch (e) { setError(e.message); }
    finally { setReseeding(false); }
  }

  return (
    <div data-testid="follower-finder-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <p className="text-slate-600 max-w-2xl">Today's slice of Canadian rental/real-estate accounts to follow + engage with. Manually follow + like 3-5 per day; the platform's algorithm will start surfacing your content to their audiences.</p>
        <button data-testid="follower-finder-reseed" onClick={reseed} disabled={reseeding} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 disabled:opacity-60">
          {reseeding ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Shuffle className="w-3.5 h-3.5"/>} Reseed pool
        </button>
      </div>

      <div className="mt-5 inline-flex bg-white rounded-xl border border-slate-200 p-1 shadow-sm flex-wrap">
        {[
          ["youtube",   "YouTube",   Youtube],
          ["instagram", "Instagram", Instagram],
          ["facebook",  "Facebook",  Facebook],
          ["linkedin",  "LinkedIn",  Linkedin],
          ["snapchat",  "Snapchat",  Ghost],
        ].map(([k, label, Icon]) => (
          <button key={k} data-testid={`follower-finder-platform-${k}`} onClick={() => setPlatform(k)} className={cn("inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors", platform === k ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900")}>
            <Icon className="w-3.5 h-3.5"/> {label}
          </button>
        ))}
      </div>

      {error && <div className="mt-4 px-4 py-3 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg">{error}</div>}

      <div className="mt-5 grid md:grid-cols-2 gap-3">
        {loading && <div className="col-span-full text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/> Loading…</div>}
        {!loading && items.length === 0 && <div className="col-span-full text-sm text-slate-500">No accounts yet — click "Reseed pool".</div>}
        {items.map((it, i) => (
          <div key={`${it.handle}-${i}`} data-testid={`follower-finder-account-${i}`} className="bg-white border border-slate-200 rounded-xl p-4 hover:border-emerald-300 transition-colors">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-bold text-slate-900 truncate">{it.name}</div>
                <div className="text-xs text-emerald-700 font-mono mt-0.5">{it.handle}</div>
              </div>
              {it.band && <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-full whitespace-nowrap">{it.band}</span>}
            </div>
            <div className="mt-2 text-xs text-slate-500 uppercase tracking-wider font-bold">{it.category}</div>
            <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">{it.angle}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// YT MAGNET tab
// ---------------------------------------------------------------------------
function MagnetTab({ token }) {
  const [topic, setTopic] = useState("");
  const [audience, setAudience] = useState("Canadian small landlords + first-time investors");
  const [length, setLength] = useState("8-12 minutes");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [out, setOut] = useState(null);
  const [copied, setCopied] = useState(null);

  async function generate() {
    if (!topic.trim()) return;
    setLoading(true); setError(""); setOut(null);
    try {
      const d = await api("/youtube/magnet", { token, method: "POST", body: { topic: topic.trim(), audience, video_length: length } });
      setOut(d);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }

  function copy(key, text) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key); setTimeout(() => setCopied(null), 1800);
    });
  }

  return (
    <div data-testid="follower-magnet-tab">
      <p className="text-slate-600">Paste a topic → smart engine returns 5 algorithm-friendly titles, a 400-700 char description, 12-18 tags, hashtags, and a smart-generated thumbnail image.</p>

      <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
        <div>
          <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Topic</label>
          <input data-testid="follower-magnet-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. How to handle a non-paying tenant in Ontario (LTB process explained)" className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"/>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Audience</label>
            <input data-testid="follower-magnet-audience" value={audience} onChange={(e) => setAudience(e.target.value)} className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"/>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Video length</label>
            <select data-testid="follower-magnet-length" value={length} onChange={(e) => setLength(e.target.value)} className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500">
              <option>3-5 minutes</option><option>5-8 minutes</option><option>8-12 minutes</option><option>12-20 minutes</option><option>20+ minutes</option>
            </select>
          </div>
        </div>
        <button data-testid="follower-magnet-generate" onClick={generate} disabled={loading || !topic.trim()} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm rounded-lg disabled:opacity-60">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin"/> Generating…</> : <><Sparkles className="w-4 h-4"/> Generate assets</>}
        </button>
        {error && <div className="text-rose-700 bg-rose-50 border border-rose-200 px-4 py-3 rounded-lg text-sm">{error}</div>}
      </div>

      {out && (
        <div className="mt-6 grid lg:grid-cols-[420px_1fr] gap-5" data-testid="follower-magnet-result">
          {/* Thumbnail */}
          <div>
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Thumbnail (16:9)</div>
            {out.thumbnail_data_uri
              ? <img src={out.thumbnail_data_uri} alt="YT thumbnail" className="w-full rounded-xl border border-slate-200 shadow-sm"/>
              : <div className="aspect-video bg-slate-100 border border-dashed border-slate-300 rounded-xl flex items-center justify-center text-slate-400 text-sm gap-2"><ImageIcon className="w-5 h-5"/> Thumbnail unavailable</div>
            }
            {out.thumbnail_data_uri && (
              <a href={out.thumbnail_data_uri} download="thumbnail.png" className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline">Download thumbnail</a>
            )}
          </div>

          {/* Titles + desc + tags */}
          <div className="space-y-5">
            <div>
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">5 Title options</div>
              <div className="space-y-2">
                {(out.titles || []).map((t, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-lg px-3 py-2">
                    <span className="text-sm text-slate-800 flex-1">{t}</span>
                    <span className="text-[11px] text-slate-400 font-mono">{t.length}c</span>
                    <button data-testid={`follower-magnet-copy-title-${i}`} onClick={() => copy(`t${i}`, t)} className={cn("inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold", copied === `t${i}` ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 hover:bg-slate-200 text-slate-700")}>
                      <Copy className="w-3 h-3"/>{copied === `t${i}` ? "Copied" : "Copy"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Description</span>
                <button data-testid="follower-magnet-copy-desc" onClick={() => copy("desc", out.description || "")} className={cn("inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold", copied === "desc" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 hover:bg-slate-200 text-slate-700")}><Copy className="w-3 h-3"/>{copied === "desc" ? "Copied" : "Copy"}</button>
              </div>
              <pre className="whitespace-pre-wrap font-sans text-sm text-slate-800 bg-white border border-slate-200 rounded-lg p-3 max-h-[260px] overflow-y-auto">{out.description}</pre>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Tags</span>
                  <button data-testid="follower-magnet-copy-tags" onClick={() => copy("tags", (out.tags || []).join(", "))} className={cn("inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold", copied === "tags" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 hover:bg-slate-200 text-slate-700")}><Copy className="w-3 h-3"/>{copied === "tags" ? "Copied" : "Copy"}</button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(out.tags || []).map((t) => <span key={t} className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{t}</span>)}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Hashtags</div>
                <div className="flex flex-wrap gap-1.5">
                  {(out.hashtags || []).map((h) => <span key={h} className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded">{h}</span>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// LISTENER tab — recent comments/mentions
// ---------------------------------------------------------------------------
function ListenerTab({ token, status }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true); setError("");
    try {
      const d = await api("/listener/inbox", { token });
      setItems(d.items || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []); // eslint-disable-line

  const anyConnected = status && (status.facebook || status.instagram || status.youtube);

  if (!anyConnected) {
    return <NotConnectedBanner feature="Listener" status={status}/>;
  }

  const platformIcon = { facebook: Facebook, instagram: Instagram, youtube: Youtube };

  return (
    <div data-testid="follower-listener-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-slate-600">Recent comments + mentions across your connected accounts (oldest first).</p>
        <button data-testid="follower-listener-refresh" onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg"><RefreshCw className={cn("w-3.5 h-3.5", loading && "animate-spin")}/> Refresh</button>
      </div>
      {error && <div className="mt-3 px-4 py-3 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg">{error}</div>}
      <div className="mt-5 space-y-3">
        {loading && items.length === 0 && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/> Loading…</div>}
        {!loading && items.length === 0 && <div className="text-sm text-slate-500">No recent activity. Quiet day.</div>}
        {items.map((c, i) => {
          const Icon = platformIcon[c.platform] || Inbox;
          return (
            <div key={i} data-testid={`follower-listener-item-${i}`} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center justify-between gap-2 text-xs">
                <div className="inline-flex items-center gap-1.5">
                  <Icon className="w-3.5 h-3.5 text-slate-500"/>
                  <span className="font-bold text-slate-900">{c.author}</span>
                  <span className="text-slate-400">on {c.platform}</span>
                </div>
                <span className="text-slate-400 font-mono">{(c.created || "").slice(0, 16).replace("T", " ")}</span>
              </div>
              <p className="mt-2 text-sm text-slate-800 leading-relaxed">{c.text}</p>
              {c.context && <div className="mt-2 text-[11px] text-slate-400 italic truncate">re: {c.context}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ANALYTICS tab
// ---------------------------------------------------------------------------
function AnalyticsTab({ token, status }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try { setData(await api("/analytics/overview", { token })); } catch { /* no-op */ }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []); // eslint-disable-line

  const anyConnected = status && (status.facebook || status.instagram || status.youtube);
  if (!anyConnected) return <NotConnectedBanner feature="Analytics" status={status}/>;

  return (
    <div data-testid="follower-analytics-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-slate-600">Live follower count + key stats across your connected accounts.</p>
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg"><RefreshCw className={cn("w-3.5 h-3.5", loading && "animate-spin")}/> Refresh</button>
      </div>
      <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          ["facebook",  "Facebook",  Facebook,  data?.facebook,  ["followers"]],
          ["instagram", "Instagram", Instagram, data?.instagram, ["followers", "media"]],
          ["youtube",   "YouTube",   Youtube,   data?.youtube,   ["subscribers", "videos", "views"]],
        ].map(([k, label, Icon, d, fields]) => (
          <div key={k} data-testid={`follower-analytics-card-${k}`} className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center gap-2"><Icon className="w-4 h-4 text-emerald-700"/><span className="font-bold text-slate-900">{label}</span></div>
            {!d?.connected ? (
              <p className="mt-3 text-xs text-slate-400">Not connected. Add credentials in Connect tab.</p>
            ) : (
              <>
                {d.name && <div className="mt-2 text-xs text-slate-500">@{d.name}</div>}
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {fields.map((f) => (
                    <div key={f}>
                      <div className="text-2xl font-bold text-slate-900">{(d[f] || 0).toLocaleString()}</div>
                      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mt-0.5">{f}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
            {d?.error && <p className="mt-2 text-[11px] text-rose-600">{d.error}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CONNECT tab — paste tokens
// ---------------------------------------------------------------------------
function ConnectField({ label, value, onChange, placeholder, type = "text", testid, hint }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm font-mono outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"/>
      {hint && <p className="mt-1 text-[11px] text-slate-500">{hint}</p>}
    </div>
  );
}

function ConnectTab({ token, status, onSaved }) {
  const [fb, setFb] = useState("");
  const [fbId, setFbId] = useState("");
  const [igId, setIgId] = useState("");
  const [ytKey, setYtKey] = useState("");
  const [ytChan, setYtChan] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  async function save() {
    setBusy(true); setMsg(""); setErr("");
    try {
      const body = {};
      if (fb)      body.facebook_page_token    = fb;
      if (fbId)    body.facebook_page_id       = fbId;
      if (igId)    body.instagram_business_id  = igId;
      if (ytKey)   body.youtube_api_key        = ytKey;
      if (ytChan)  body.youtube_channel_id     = ytChan;
      if (!Object.keys(body).length) { setErr("Paste at least one credential"); setBusy(false); return; }
      const d = await api("/tokens/save", { token, method: "POST", body });
      setMsg(`Saved: ${(d.saved_keys || []).filter((k) => k !== "updated_at").join(", ")}`);
      setFb(""); setFbId(""); setIgId(""); setYtKey(""); setYtChan("");
      if (onSaved) onSaved();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div data-testid="follower-connect-tab">
      <p className="text-slate-600">Connect each platform once. Tokens are stored encrypted in your own database — never sent off-server.</p>

      {/* Status pills */}
      <div className="mt-5 grid grid-cols-3 gap-3">
        {[
          ["facebook",  "Facebook",  Facebook],
          ["instagram", "Instagram", Instagram],
          ["youtube",   "YouTube",   Youtube],
        ].map(([k, label, Icon]) => (
          <div key={k} className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3">
            <Icon className="w-5 h-5 text-slate-500"/>
            <div className="flex-1">
              <div className="font-bold text-slate-900 text-sm">{label}</div>
              {status?.[k]
                ? <div className="inline-flex items-center gap-1 text-xs text-emerald-700 font-semibold"><CheckCircle2 className="w-3.5 h-3.5"/> Connected</div>
                : <div className="inline-flex items-center gap-1 text-xs text-slate-400 font-semibold"><AlertCircle className="w-3.5 h-3.5"/> Not connected</div>
              }
            </div>
          </div>
        ))}
      </div>

      {/* Form */}
      <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5 space-y-5">
        <div>
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2"><Facebook className="w-4 h-4"/> Facebook + Instagram</h3>
          <p className="mt-1 text-xs text-slate-500">From Meta Business → Settings → System users → generate a long-lived Page Access Token with <code>pages_read_engagement</code> + <code>instagram_basic</code> permissions.</p>
          <div className="mt-3 space-y-3">
            <ConnectField label="Facebook Page Access Token" value={fb} onChange={setFb} type="password" placeholder="EAAB…" testid="follower-connect-fb-token"/>
            <ConnectField label="Facebook Page ID" value={fbId} onChange={setFbId} placeholder="123456789012345" testid="follower-connect-fb-id"/>
            <ConnectField label="Instagram Business ID" value={igId} onChange={setIgId} placeholder="17841405822304914" testid="follower-connect-ig-id" hint="Found via Graph API Explorer: GET /me/accounts → instagram_business_account.id"/>
          </div>
        </div>
        <div className="pt-4 border-t border-slate-200">
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2"><Youtube className="w-4 h-4"/> YouTube</h3>
          <p className="mt-1 text-xs text-slate-500">Create an API key at <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline">console.cloud.google.com</a> → enable "YouTube Data API v3" → restrict to that API.</p>
          <div className="mt-3 space-y-3">
            <ConnectField label="YouTube Data API key" value={ytKey} onChange={setYtKey} type="password" placeholder="AIza…" testid="follower-connect-yt-key"/>
            <ConnectField label="YouTube Channel ID" value={ytChan} onChange={setYtChan} placeholder="UCxxxxxxxxxxxxxxxxxxxxxx" testid="follower-connect-yt-chan" hint="From youtube.com/account_advanced → Channel ID (24 chars starting with UC)"/>
          </div>
        </div>
        <button data-testid="follower-connect-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm rounded-lg disabled:opacity-60">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Saving…</> : <>Save credentials</>}
        </button>
        {msg && <div className="text-emerald-700 bg-emerald-50 border border-emerald-200 px-4 py-3 rounded-lg text-sm">{msg}</div>}
        {err && <div className="text-rose-700 bg-rose-50 border border-rose-200 px-4 py-3 rounded-lg text-sm">{err}</div>}
      </div>
    </div>
  );
}

function NotConnectedBanner({ feature, status }) {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6" data-testid="follower-not-connected">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-700 mt-0.5"/>
        <div>
          <h3 className="font-bold text-amber-900">{feature} needs API access</h3>
          <p className="mt-1 text-sm text-amber-800">
            None of FB / IG / YT are connected yet. Switch to the <b>Connect</b> tab to paste credentials. Until then, this view stays empty.
          </p>
          {status && (
            <div className="mt-3 text-xs text-amber-700 space-x-3">
              {["facebook", "instagram", "youtube"].map((k) => (
                <span key={k} className={status[k] ? "text-emerald-700 font-semibold" : ""}>{k}: {status[k] ? "✓" : "—"}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LINKEDIN OUTREACH tab — daily prospect list + Smart-drafted DMs
// ---------------------------------------------------------------------------
function OutreachTab({ token }) {
  const [province, setProvince] = useState("Ontario");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(null);

  async function generate() {
    setLoading(true); setError("");
    try {
      const d = await api("/linkedin/outreach", { token, method: "POST", body: { province, count: 8 } });
      setItems(d.items || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { generate(); /* eslint-disable-next-line */ }, []);

  function copy(key, text) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key); setTimeout(() => setCopied(null), 1800);
    });
  }

  return (
    <div data-testid="follower-outreach-tab">
      <p className="text-slate-600">Daily LinkedIn outreach guide — prospect archetypes, the exact LinkedIn search to run, and a tailored connection-request message for each. Send 3-5/day from the bottom of this list.</p>

      <div className="mt-5 flex items-end gap-3 flex-wrap">
        <div>
          <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Province focus</label>
          <select value={province} onChange={(e) => setProvince(e.target.value)} data-testid="follower-outreach-province" className="mt-1 block px-3 py-2 border border-slate-300 rounded-lg text-sm">
            {["Ontario","British Columbia","Alberta","Quebec","Manitoba","Saskatchewan","Nova Scotia","New Brunswick"].map((p) => <option key={p}>{p}</option>)}
          </select>
        </div>
        <button data-testid="follower-outreach-generate" onClick={generate} disabled={loading} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm rounded-lg disabled:opacity-60">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin"/> Drafting…</> : <><Target className="w-4 h-4"/> Generate guide</>}
        </button>
      </div>

      {error && <div className="mt-4 px-4 py-3 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg">{error}</div>}

      <div className="mt-5 space-y-3">
        {items.map((it, i) => (
          <div key={i} data-testid={`follower-outreach-item-${i}`} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-bold text-slate-900">{it.archetype}</div>
                <div className="text-xs text-slate-500 mt-0.5">{it.why}</div>
              </div>
            </div>
            <div className="mt-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">LinkedIn search</span>
                <button data-testid={`follower-outreach-copy-q-${i}`} onClick={() => copy(`q${i}`, it.search_query)} className={cn("inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded", copied === `q${i}` ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 hover:bg-slate-200 text-slate-600")}><Copy className="w-3 h-3"/>{copied === `q${i}` ? "Copied" : "Copy"}</button>
              </div>
              <pre className="mt-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded font-mono text-xs text-slate-700 whitespace-pre-wrap">{it.search_query}</pre>
            </div>
            <div className="mt-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Connection message ({it.connection_message.length}c)</span>
                <button data-testid={`follower-outreach-copy-m-${i}`} onClick={() => copy(`m${i}`, it.connection_message)} className={cn("inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded", copied === `m${i}` ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 hover:bg-slate-200 text-slate-600")}><Copy className="w-3 h-3"/>{copied === `m${i}` ? "Copied" : "Copy"}</button>
              </div>
              <p className="mt-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded text-sm text-slate-800 leading-relaxed">{it.connection_message}</p>
            </div>
          </div>
        ))}
        {!loading && items.length === 0 && <div className="text-sm text-slate-500">Click "Generate guide" to start.</div>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// GROWTH PLAN tab — weekly summary across all platforms
// ---------------------------------------------------------------------------
function GrowthPlanTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  async function load() {
    setLoading(true);
    try { setData(await api("/growth/plan", { token })); } catch { /* no-op */ }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading) return <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/> Loading plan…</div>;
  if (!data) return null;
  const platformIcon = { youtube: Youtube, instagram: Instagram, linkedin: Linkedin, facebook: Facebook, snapchat: Ghost };

  return (
    <div data-testid="follower-growth-tab">
      <p className="text-slate-600">This week's actionable guide. Aim for ~30 min/day across the three blocks below — compounds fast.</p>

      <div className="mt-5 grid sm:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="text-xs text-slate-500 uppercase tracking-wider font-bold">Content queue</div>
          <div className="mt-2 text-3xl font-bold text-slate-900">{data.queue.pending + data.queue.approved}</div>
          <div className="text-xs text-slate-500 mt-1">{data.queue.approved} approved · {data.queue.pending} pending</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="text-xs text-slate-500 uppercase tracking-wider font-bold">Accounts to engage</div>
          <div className="mt-2 text-3xl font-bold text-slate-900">{Object.values(data.engage_today).flat().length}</div>
          <div className="text-xs text-slate-500 mt-1">across 5 platforms</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="text-xs text-slate-500 uppercase tracking-wider font-bold">Week of</div>
          <div className="mt-2 text-lg font-bold text-slate-900">{data.week_of}</div>
          <div className="text-xs text-slate-500 mt-1">UTC</div>
        </div>
      </div>

      <div className="mt-6">
        <h3 className="text-sm font-bold text-slate-900">Engage today (sample)</h3>
        <div className="mt-3 grid md:grid-cols-2 gap-3">
          {Object.entries(data.engage_today).map(([plat, accounts]) => {
            const Icon = platformIcon[plat] || Compass;
            return (
              <div key={plat} className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2"><Icon className="w-4 h-4 text-emerald-700"/><span className="font-bold text-slate-900 capitalize">{plat}</span><span className="text-xs text-slate-400">· {accounts.length}</span></div>
                {accounts.length === 0
                  ? <div className="text-xs text-slate-400">Pool not seeded yet — visit Finder → {plat} to populate.</div>
                  : (
                    <ul className="text-xs text-slate-700 space-y-1">
                      {accounts.slice(0, 5).map((a, i) => (
                        <li key={i} className="truncate"><b>{a.name}</b> <span className="text-slate-400 font-mono">{a.handle}</span></li>
                      ))}
                    </ul>
                  )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2"><ListChecks className="w-4 h-4 text-emerald-700"/>Tips of the week</h3>
        <ul className="mt-2 text-sm text-slate-700 space-y-1.5">
          {data.tips.map((t, i) => <li key={i} className="flex gap-2"><span className="text-emerald-700 mt-1">▸</span><span>{t}</span></li>)}
        </ul>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TACTICS tab — weekly platform-specific growth moves
// ---------------------------------------------------------------------------
function TacticsTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { setData(await api("/growth/tactics", { token })); } catch { /* no-op */ }
      finally { setLoading(false); }
    })();
  }, [token]);

  if (loading) return <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/> Loading this week's tactics (first call may take ~45s)…</div>;
  if (!data) return <div className="text-sm text-slate-500">Tactics are running in offline mode — using built-in guidance templates.</div>;

  const platformMeta = {
    linkedin:  { label: "LinkedIn",  Icon: Linkedin,  color: "bg-sky-50 text-sky-700 border-sky-200" },
    facebook:  { label: "Facebook",  Icon: Facebook,  color: "bg-blue-50 text-blue-700 border-blue-200" },
    instagram: { label: "Instagram", Icon: Instagram, color: "bg-pink-50 text-pink-700 border-pink-200" },
    tiktok:    { label: "TikTok",    Icon: Compass,   color: "bg-slate-100 text-slate-900 border-slate-300" },
    snapchat:  { label: "Snapchat",  Icon: Ghost,     color: "bg-amber-50 text-amber-700 border-amber-200" },
  };

  return (
    <div data-testid="follower-tactics-tab">
      <p className="text-slate-600">Three SPECIFIC growth tactics per platform for <b>{data.week}</b>. Each tactic includes an expected outcome — pick 1-2 per platform per week and execute. No bot follows, no engagement pods.</p>

      <div className="mt-5 space-y-6">
        {Object.entries(data.by_platform).map(([plat, tactics]) => {
          const m = platformMeta[plat] || { label: plat, Icon: Compass, color: "bg-slate-50 text-slate-700 border-slate-200" };
          const Icon = m.Icon;
          return (
            <div key={plat} data-testid={`follower-tactics-${plat}`}>
              <div className="flex items-center gap-2 mb-3">
                <Icon className="w-5 h-5 text-emerald-700"/>
                <h3 className="font-display text-xl font-bold text-slate-900">{m.label}</h3>
                <span className={cn("text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border", m.color)}>{tactics.length} tactics</span>
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                {tactics.map((t, i) => (
                  <div key={i} className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Tactic {i + 1}</div>
                    <p className="mt-2 text-sm text-slate-800 leading-relaxed">{t.tactic}</p>
                    {t.why && <p className="mt-2 text-xs text-slate-500 leading-relaxed"><b>Why:</b> {t.why}</p>}
                    {t.expected_lift && <p className="mt-2 text-xs text-emerald-700 leading-relaxed border-t border-slate-100 pt-2"><b>Expected lift:</b> {t.expected_lift}</p>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------
const TABS = [
  { key: "growth",    label: "Growth plan", icon: ListChecks },
  { key: "tactics",   label: "Tactics",     icon: Target },
  { key: "listener",  label: "Listener",  icon: Inbox },
  { key: "analytics", label: "Analytics", icon: BarChart3 },
  { key: "finder",    label: "Finder",    icon: Compass },
  { key: "outreach",  label: "LinkedIn",  icon: Linkedin },
  { key: "magnet",    label: "YT Magnet", icon: Youtube },
  { key: "connect",   label: "Connect",   icon: Settings },
];

export default function FollowerAdmin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [tab, setTab] = useState("growth");
  const [status, setStatus] = useState(null);

  async function loadStatus() {
    if (!token) return;
    try { setStatus(await api("/tokens/status", { token })); }
    catch (e) {
      if (e.message === "unauthorized") { setToken(""); localStorage.removeItem(TOKEN_KEY); }
    }
  }
  useEffect(() => { loadStatus(); }, [token]); // eslint-disable-line

  const anyConnected = useMemo(() => !!status && (status.facebook || status.instagram || status.youtube), [status]);

  if (!token) {
    return <LockScreen onUnlock={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }}/>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-10">
        <div className="flex items-start justify-between gap-5 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-emerald-700"><Sparkles className="w-4 h-4"/><span className="text-xs uppercase tracking-[0.2em] font-bold">Smart Follower</span></div>
            <h1 className="mt-2 font-display text-3xl md:text-4xl font-bold tracking-tighter text-slate-900 leading-tight">Audience engine</h1>
            <p className="mt-2 text-sm text-slate-500">Grow @centralrentals on Facebook, Instagram + YouTube. {anyConnected ? <span className="text-emerald-700 font-semibold">Live</span> : <span className="text-slate-400">Add credentials to unlock Listener + Analytics</span>}</p>
          </div>
          <button data-testid="follower-logout-btn" onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }} className="text-xs text-slate-400 hover:text-slate-700 hover:underline">Lock</button>
        </div>

        <div className="mt-6 flex items-center gap-1 border-b border-slate-200 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} data-testid={`follower-mainTab-${t.key}`} onClick={() => setTab(t.key)} className={cn("inline-flex items-center gap-1.5 px-4 py-3 text-sm font-semibold transition-colors -mb-px border-b-2 whitespace-nowrap", tab === t.key ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-800")}><Icon className="w-4 h-4"/>{t.label}</button>
            );
          })}
          <a href="/admin/viralpost" className="ml-auto inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-700 px-3"><ExternalLink className="w-3.5 h-3.5"/> ViralPost</a>
        </div>

        <div className="mt-7">
          {tab === "growth"    && <GrowthPlanTab token={token}/>}
          {tab === "tactics"   && <TacticsTab    token={token}/>}
          {tab === "listener"  && <ListenerTab  token={token} status={status}/>}
          {tab === "analytics" && <AnalyticsTab token={token} status={status}/>}
          {tab === "finder"    && <FinderTab    token={token}/>}
          {tab === "outreach"  && <OutreachTab  token={token}/>}
          {tab === "magnet"    && <MagnetTab    token={token}/>}
          {tab === "connect"   && <ConnectTab   token={token} status={status} onSaved={loadStatus}/>}
        </div>
      </div>
    </div>
  );
}
